// CB:BEGIN p0-fr-016-017-022-project-flow
/**
 * P0: Escrow, signing, start gate, roof color, start project, milestone evidence (FR-026).
 * Phase 1.3 (FR-022 frontend): Fund escrow, balance/milestones, admin pause + release history.
 * Phase 6.3 (FR-016 frontend): Gate checklist, Start button + tooltip, roof color, admin override, contractor message.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useApi } from '../lib/useApi';
import { useAuth } from '@clerk/clerk-react';
import MilestoneDisputeModal from './MilestoneDisputeModal';

/** Map API milestone status to Phase 1.3.2 display labels */
function milestoneStatusLabel(status: string): string {
  const map: Record<string, string> = {
    PENDING: 'Pending',
    EVIDENCE_SUBMITTED: 'Evidence submitted',
    APPROVED: 'Approved',
    RELEASED: 'Released',
  };
  return map[status] ?? status;
}

/** P0 3-milestone keys -> display labels (25/25/50) */
const MILESTONE_KEY_LABELS: Record<string, string> = {
  M1_START: 'Project Start (25%)',
  M2_DEMO: 'Demolition Complete (25%)',
  M3_COMPLETE: 'Project Complete (50%)',
  M1: 'Project Start',
  M2: 'Demolition Complete',
  M3: 'Project Complete',
};
function milestoneKeyLabel(key: string): string {
  return MILESTONE_KEY_LABELS[key] ?? key;
}

interface P0ProjectFlowProps {
  projectId: string;
  /** 'homeowner' | 'contractor' | 'admin' - controls which actions are shown */
  role: 'homeowner' | 'contractor' | 'admin';
  onUpdated?: () => void;
  /** Optional: navigate to payment-screen for "Fund escrow" (Phase 1.3.1). Caller should set active_project_id first. */
  onNavigate?: (screen: string) => void;
}

interface EscrowState {
  id: string;
  status: string;
  totalAmountCents: number;
  releasedCents: number;
  balanceCents: number;
  isPaused: boolean;
  milestones: { id: string; milestoneKey: string; amountCents: number; status: string; releasedAt?: string }[];
}

interface GateState {
  passed: boolean;
  conditions: {
    depositCaptured?: boolean;
    escrowFunded?: boolean;
    colorSelected: boolean;
    contractSigned: boolean;
    milestone1Defined: boolean;
    scheduledStartSet?: boolean;
  };
  scheduledStartAt?: string | null;
  depositCapturedAt?: string | null;
}

interface EvidenceItem {
  id: string;
  status: string;
  fileKeys: string[];
  submittedAt?: string;
}

interface SigningState {
  homeownerSigned: boolean;
  contractorSigned: boolean;
  fullySigned: boolean;
  homeownerSignedAt?: string;
  contractorSignedAt?: string;
}

export const P0ProjectFlow: React.FC<P0ProjectFlowProps> = ({ projectId, role, onUpdated, onNavigate }) => {
  const { get, post, put } = useApi();
  const { getToken } = useAuth();
  const [escrow, setEscrow] = useState<EscrowState | null>(null);
  const [gate, setGate] = useState<GateState | null>(null);
  const [signing, setSigning] = useState<SigningState | null>(null);
  const [evidenceByMilestone, setEvidenceByMilestone] = useState<Record<string, { evidence: EvidenceItem[] }>>({});
  const [roofColor, setRoofColor] = useState('');
  // CB:BEGIN canonical-color-options — canonical color options fetched from API
  const [availableColors, setAvailableColors] = useState<Array<{ name: string; available: boolean }>>([]);
  const [colorOptionsFromAPI, setColorOptionsFromAPI] = useState<Array<{ id: string; colorNameDisplay: string; active: boolean; sortOrder: number; distributorLabel: string | null }>>([]);
  const [selectedColorOptionId, setSelectedColorOptionId] = useState('');
  // CB:END canonical-color-options
  const [materialSpec, setMaterialSpec] = useState<{ manufacturer?: string; productLine?: string; specUrl?: string; materialType?: string; bidId?: string; colors?: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [disputeModal, setDisputeModal] = useState<{ milestoneKey: string; milestoneName: string } | null>(null);
  const [adminOverrideReason, setAdminOverrideReason] = useState('');
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [scheduledStartInput, setScheduledStartInput] = useState('');
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // CB:BEGIN contractor-contract-view
  const [contractorContractPdfUrl, setContractorContractPdfUrl] = useState<string | null>(null);
  const [contractorContractPdfFilename, setContractorContractPdfFilename] = useState<string | null>(null);
  const contractorPdfRequestedForRef = useRef<string | null>(null);
  // CB:END contractor-contract-view

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [e, g, s, projectData, colorOptsRes] = await Promise.all([
        get(`/api/projects/${projectId}/escrow`).catch(() => null),
        get(`/api/projects/${projectId}/start-gate`).catch(() => null),
        get(`/api/projects/${projectId}/signing-status`).catch(() => null),
        get(`/api/projects/${projectId}`).catch(() => null),
        // CB:BEGIN canonical-color-fetch
        get(`/api/projects/${projectId}/color-options`).catch(() => null),
        // CB:END canonical-color-fetch
      ]);
      setEscrow(e ?? null);
      setGate(g ?? null);
      setSigning(s ?? null);

      // Show homeowner-provided data (roof color, scheduled start) so contractor sees what was set
      if (projectData?.roofColor) setRoofColor(String(projectData.roofColor).trim());

      // CB:BEGIN canonical-color-options-load
      // Prefer canonical color options from API; fall back to legacy availableColors from bid JSON
      if (colorOptsRes?.colors && Array.isArray(colorOptsRes.colors) && colorOptsRes.colors.length > 0) {
        setColorOptionsFromAPI(colorOptsRes.colors);
      }
      // CB:END canonical-color-options-load

      // Extract material specification from accepted bid
      if (projectData?.bids) {
        const acceptedBid = projectData.bids.find((b: any) => b.isAccepted);
        if (acceptedBid) {
          if (acceptedBid.availableColors && Array.isArray(acceptedBid.availableColors)) {
            setAvailableColors(acceptedBid.availableColors);
          }
          if (acceptedBid.materialManufacturer || acceptedBid.materialProductLine || acceptedBid.materialSpecUrl || acceptedBid.materialType) {
            // Extract color names from canonical color options or legacy availableColors from note
            let colorNames: string[] = [];
            if (acceptedBid.colorOptions && Array.isArray(acceptedBid.colorOptions)) {
              colorNames = (acceptedBid.colorOptions as any[]).map((c: any) => c.colorNameDisplay || c.name || String(c)).filter(Boolean);
            } else if (acceptedBid.availableColors && Array.isArray(acceptedBid.availableColors)) {
              colorNames = (acceptedBid.availableColors as any[]).map((c: any) => c.name || String(c)).filter(Boolean);
            } else if (acceptedBid.note && typeof acceptedBid.note === 'string') {
              try {
                const n = JSON.parse(acceptedBid.note);
                if (Array.isArray(n?.availableColors)) {
                  colorNames = n.availableColors.map((c: any) => c.name || String(c)).filter(Boolean);
                }
              } catch {}
            }
            setMaterialSpec({
              manufacturer: acceptedBid.materialManufacturer,
              productLine: acceptedBid.materialProductLine,
              specUrl: acceptedBid.materialSpecUrl,
              materialType: acceptedBid.materialType,
              bidId: acceptedBid.id,
              colors: colorNames.length > 0 ? colorNames : undefined,
            });
          }
        }
      }
      if (g?.scheduledStartAt) setScheduledStartInput(g.scheduledStartAt.slice(0, 16));
      if (e?.milestones?.length) {
        const evRes = await Promise.all(
          (e.milestones as { milestoneKey: string }[]).map((m) =>
            get(`/api/projects/${projectId}/milestones/${m.milestoneKey}/evidence`)
              .then((r: any) => ({ key: m.milestoneKey, evidence: r?.evidence ?? [] }))
              .catch(() => ({ key: m.milestoneKey, evidence: [] }))
          )
        );
        const next: Record<string, { evidence: EvidenceItem[] }> = {};
        evRes.forEach(({ key, evidence }) => {
          next[key] = { evidence: Array.isArray(evidence) ? evidence : [] };
        });
        setEvidenceByMilestone(next);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  // Preload contractor contract PDF URL once per project when contract section is relevant (ref guard prevents repeated requests)
  useEffect(() => {
    if (!projectId || !signing || (role !== 'contractor' && role !== 'admin')) return;
    if (contractorPdfRequestedForRef.current === projectId) return;
    contractorPdfRequestedForRef.current = projectId;
    let cancelled = false;
    get(`/api/projects/${projectId}/contract/contractor-pdf`, { dedupeKey: `contractor-pdf-${projectId}` })
      .then((res: any) => {
        if (!cancelled && res?.signedUrl) {
          setContractorContractPdfUrl(res.signedUrl);
          if (res?.filename) setContractorContractPdfFilename(res.filename);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // Intentionally omit get to avoid re-running every render (useApi returns new refs each time)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, !!signing, role]);

  const handleSetScheduledStart = async () => {
    if (!scheduledStartInput.trim()) return;
    setActionLoading('scheduled-start');
    setError(null);
    try {
      const d = new Date(scheduledStartInput);
      if (Number.isNaN(d.getTime())) throw new Error('Invalid date');
      await put(`/api/projects/${projectId}/scheduled-start`, { scheduledStartAt: d.toISOString() });
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to set start date');
    } finally {
      setActionLoading(null);
    }
  };

  // CB:BEGIN canonical-roof-color-handler
  const handleRoofColor = async () => {
    // Canonical path: colorOptionId selected from dropdown
    if (selectedColorOptionId) {
      setActionLoading('roof-color');
      try {
        await put(`/api/projects/${projectId}/roof-color`, { colorOptionId: selectedColorOptionId });
        setSelectedColorOptionId('');
        await load();
        onUpdated?.();
      } catch (err: any) {
        setError(err?.message ?? 'Failed to set roof color');
      } finally {
        setActionLoading(null);
      }
      return;
    }
    // Legacy free-text path
    if (!roofColor.trim()) return;
    setActionLoading('roof-color');
    try {
      await put(`/api/projects/${projectId}/roof-color`, { roofColor: roofColor.trim() });
      setRoofColor('');
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to set roof color');
    } finally {
      setActionLoading(null);
    }
  };
  // CB:END canonical-roof-color-handler

  const handleSign = async (party: 'HOMEOWNER' | 'CONTRACTOR') => {
    setActionLoading(`sign-${party}`);
    try {
      await post(`/api/projects/${projectId}/sign`, { party });
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to sign');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStart = async (overrideReason?: string) => {
    setActionLoading('start');
    try {
      const res = await post<{ started: boolean; alreadyStarted?: boolean; failingConditions?: GateState['conditions'] }>(
        `/api/projects/${projectId}/start`,
        overrideReason ? { overrideReason } : {}
      );
      if (!res.started && res.failingConditions) setError('Gate not passed: ' + Object.entries(res.failingConditions).filter(([, v]) => !v).map(([k]) => k).join(', '));
      else await load();
      onUpdated?.();
      setShowOverrideModal(false);
      setAdminOverrideReason('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to start project');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseEscrow = async () => {
    setActionLoading('pause-escrow');
    try {
      await post(`/api/admin/projects/${projectId}/pause-escrow`, {});
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to pause escrow');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeEscrow = async () => {
    setActionLoading('resume-escrow');
    try {
      await post(`/api/admin/projects/${projectId}/resume-escrow`, {});
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to resume escrow');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFundEscrow = () => {
    if (!projectId || !onNavigate) return;
    try {
      localStorage.setItem('active_project_id', projectId);
    } catch (_) {}
    onNavigate('payment-screen');
  };

  const handleEvidenceUpload = async (milestoneKey: string) => {
    const input = fileInputRefs.current[milestoneKey];
    if (!input?.files?.length || !projectId) return;
    setActionLoading(`evidence-${milestoneKey}`);
    setError(null);
    try {
      const { getApiUrl } = await import('../lib/getApiUrl');
      const token = await getToken();
      const form = new FormData();
      for (let i = 0; i < input.files.length; i++) form.append('files', input.files[i]);
      const res = await fetch(getApiUrl(`/api/projects/${projectId}/milestones/${milestoneKey}/evidence/upload`), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error ?? 'Upload failed');
      }
      input.value = '';
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to submit evidence');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveEvidence = async (milestoneKey: string, approved: boolean) => {
    setActionLoading(`approve-${milestoneKey}`);
    setError(null);
    try {
      await post(`/api/projects/${projectId}/milestones/${milestoneKey}/approve`, { approved });
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReleaseMilestone = async (milestoneKey: string, amountCents: number) => {
    if (!escrow?.id) return;
    setActionLoading(`release-${milestoneKey}`);
    setError(null);
    try {
      const { getApiUrl } = await import('../lib/getApiUrl');
      const token = await getToken();
      const idempotencyKeyRelease = `release-${escrow.id}-${milestoneKey}-${Date.now()}`;
      const res = await fetch(getApiUrl(`/api/escrow/v2/${escrow.id}/milestones/${encodeURIComponent(milestoneKey)}/release`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ idempotencyKeyRelease, amountCents }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error ?? 'Release failed');
      }
      await load();
      onUpdated?.();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to release milestone');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="p0-flow-loading">Loading project status…</div>;
  if (error) return <div className="p0-flow-error" role="alert">{error}</div>;

  const canEdit = role === 'homeowner' || role === 'admin';
  const showStart = canEdit && gate && (gate.passed || role === 'admin');
  const escrowNotFunded = escrow && escrow.status !== 'FUNDED';
  const missingConditions = gate
    ? (Object.entries(gate.conditions).filter(([, v]) => !v).map(([k]) => k) as (keyof GateState['conditions'])[])
    : [];
  const releaseHistory = escrow?.milestones?.filter((m) => m.status === 'RELEASED') ?? [];

  const sectionStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  };
  const sectionTitleStyle: React.CSSProperties = { fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', color: '#111827' };

  return (
    <div className="p0-project-flow" data-testid="p0-project-flow" style={{ maxWidth: 720 }}>
      {/* Escrow — Phase 1.3.1 Fund escrow; 1.3.2 balance + milestones; 1.3.3 admin pause + release history */}
      {escrow && (
        <section className="p0-escrow" style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Escrow</h3>
          <p style={{ margin: '0 0 12px 0', fontSize: 14 }}>Status: <strong>{escrow.status}</strong> · Balance: ${((escrow.balanceCents ?? escrow.totalAmountCents ?? 0) / 100).toFixed(2)}</p>
          {escrow.isPaused && <p className="p0-paused">Releases paused (dispute).</p>}
          {role === 'homeowner' && escrowNotFunded && onNavigate && (
            <p>
              <button type="button" className="btn btn-primary" disabled={!!actionLoading} onClick={handleFundEscrow}>
                Fund escrow
              </button>
              <span style={{ marginLeft: 8, fontSize: 14, color: '#6b7280' }}>Amount from your invoice/quote; you’ll complete payment via Stripe.</span>
            </p>
          )}
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
            {escrow.milestones.map((m) => (
              <li key={m.id}>
                {milestoneKeyLabel(m.milestoneKey)}: ${(m.amountCents / 100).toFixed(2)} — {milestoneStatusLabel(m.status)}
              </li>
            ))}
          </ul>
          {role === 'admin' && (
            <>
              <div style={{ marginTop: 12 }}>
                {escrow.isPaused ? (
                  <button type="button" disabled={!!actionLoading} onClick={handleResumeEscrow}>Resume releases</button>
                ) : (
                  <button type="button" disabled={!!actionLoading} onClick={handlePauseEscrow}>Pause releases</button>
                )}
              </div>
              {releaseHistory.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Release history</h4>
                  <ul style={{ fontSize: 14, margin: 0, paddingLeft: 20 }}>
                    {releaseHistory.map((m) => (
                      <li key={m.id}>
                        {milestoneKeyLabel(m.milestoneKey)}: ${(m.amountCents / 100).toFixed(2)} released{m.releasedAt ? ` at ${new Date(m.releasedAt).toLocaleString()}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          {/* CB:BEGIN p0-fr-026-evidence-ui — Contractor upload, homeowner approve */}
          {(role === 'contractor' || role === 'homeowner' || role === 'admin') && escrow.milestones.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Milestone Evidence</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {escrow.milestones.map((m) => {
                const isReleased = m.status === 'RELEASED';
                const ev = evidenceByMilestone[m.milestoneKey]?.evidence ?? [];
                const pendingApproval = ev.some((e) => e.status === 'PENDING_APPROVAL');
                const isContractor = role === 'contractor' || role === 'admin';
                const isHomeowner = role === 'homeowner' || role === 'admin';
                const amountStr = (m.amountCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

                const statusColors: Record<string, { bg: string; color: string }> = {
                  PENDING: { bg: '#f3f4f6', color: '#6b7280' },
                  EVIDENCE_SUBMITTED: { bg: '#eff6ff', color: '#2563eb' },
                  APPROVED: { bg: '#f0fdf4', color: '#16a34a' },
                  RELEASED: { bg: '#f0fdf4', color: '#15803d' },
                };
                const statusStyle = statusColors[m.status] ?? statusColors.PENDING;

                return (
                  <div key={m.id} style={{ padding: '14px 16px', border: `1px solid ${isReleased ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 8, background: isReleased ? '#f0fdf4' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                          {milestoneKeyLabel(m.milestoneKey)}
                        </span>
                        <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 6 }}>{amountStr}</span>
                        {ev.length > 0 && (
                          <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
                            · {ev.length} submission{ev.length !== 1 ? 's' : ''}, {ev[0]?.fileKeys?.length ?? 0} file(s)
                          </span>
                        )}
                      </div>
                      <span style={{
                        fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 20,
                        background: statusStyle.bg, color: statusStyle.color,
                      }}>
                        {milestoneStatusLabel(m.status)}
                      </span>
                    </div>

                    {isReleased && (
                      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, color: '#15803d', fontSize: 13 }}>
                        <span>✅</span>
                        <span>
                          <strong>{amountStr}</strong> paid out to contractor
                          {m.contractorPayoutId && (
                            <span style={{ marginLeft: 8, color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
                              · Stripe: {m.contractorPayoutId}
                            </span>
                          )}
                          {m.releasedAt && (
                            <span style={{ marginLeft: 8, color: '#9ca3af', fontSize: 12 }}>
                              · {new Date(m.releasedAt).toLocaleDateString()}
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {!isReleased && isContractor && (m.status === 'PENDING' || m.status === 'EVIDENCE_SUBMITTED') && (
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <label style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                          border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
                          fontSize: 13, fontWeight: 500, color: '#374151', background: '#f9fafb',
                        }}>
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 16v1a1 1 0 001 1h10a1 1 0 001-1v-1M12 4l-4 4m0 0l4 4m-4-4h8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Choose file(s)
                          <input
                            type="file"
                            ref={(el) => { fileInputRefs.current[m.milestoneKey] = el; }}
                            accept=".pdf,.jpg,.jpeg,.png,.heic"
                            multiple
                            style={{ display: 'none' }}
                          />
                        </label>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: 13, padding: '7px 16px' }}
                          disabled={!!actionLoading}
                          onClick={() => handleEvidenceUpload(m.milestoneKey)}
                        >
                          {actionLoading === `evidence-${m.milestoneKey}` ? 'Uploading…' : 'Submit evidence'}
                        </button>
                      </div>
                    )}

                    {!isReleased && isHomeowner && pendingApproval && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: 13, padding: '7px 16px' }}
                          disabled={!!actionLoading}
                          onClick={() => handleApproveEvidence(m.milestoneKey, true)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 13, padding: '7px 16px' }}
                          disabled={!!actionLoading}
                          onClick={() => handleApproveEvidence(m.milestoneKey, false)}
                        >
                          Request more
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 13, padding: '7px 16px', borderColor: '#dc2626', color: '#dc2626' }}
                          disabled={!!actionLoading}
                          onClick={() => setDisputeModal({ milestoneKey: m.milestoneKey, milestoneName: milestoneKeyLabel(m.milestoneKey) })}
                        >
                          Dispute this milestone
                        </button>
                      </div>
                    )}

                    {!isReleased && role === 'admin' && ev.some((e) => e.status === 'APPROVED') && (
                      <div style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: 13, padding: '7px 16px' }}
                          disabled={!!actionLoading || escrow?.isPaused}
                          onClick={() => handleReleaseMilestone(m.milestoneKey, m.amountCents)}
                        >
                          Release {milestoneKeyLabel(m.milestoneKey)}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          )}
          {/* CB:END p0-fr-026-evidence-ui */}
        </section>
      )}

      {/* Signing — contractor sees project-specific contract (like homeowner) with View/Download */}
      {signing && (
        <section className="p0-signing" style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Contract</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 12 }}>
            {role === 'admin' && (
              <span style={{ fontSize: 14 }}>Homeowner signed: <strong>{signing.homeownerSigned ? 'Yes' : 'No'}</strong></span>
            )}
            <span style={{ fontSize: 14 }}>Contractor signed: <strong>{signing.contractorSigned ? 'Yes' : 'No'}</strong></span>
          </div>
          {(role === 'contractor' || role === 'admin') && (
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 14 }}
                onClick={async () => {
                  if (contractorContractPdfUrl) {
                    window.open(contractorContractPdfUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  setActionLoading('contract-pdf');
                  try {
                    const res = await get(`/api/projects/${projectId}/contract/contractor-pdf`);
                    const url = (res as { signedUrl?: string; filename?: string }).signedUrl;
                    if (url) {
                      setContractorContractPdfUrl(url);
                      if ((res as { filename?: string }).filename) setContractorContractPdfFilename((res as { filename?: string }).filename);
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }
                  } catch {
                    setError('Could not load contract');
                  } finally {
                    setActionLoading(null);
                  }
                }}
                disabled={!!actionLoading}
              >
                View contract
              </button>

            </div>
          )}
          {signing.fullySigned && <p style={{ margin: 0, fontSize: 14, color: '#059669' }}><strong>Contract fully signed.</strong></p>}
          {canEdit && !signing.homeownerSigned && role === 'homeowner' && (
            <button type="button" className="btn btn-primary" disabled={!!actionLoading} onClick={() => handleSign('HOMEOWNER')}>I agree (Homeowner)</button>
          )}
          {(role === 'contractor' || role === 'admin') && !signing.contractorSigned && (
            <button type="button" className="btn btn-primary" disabled={!!actionLoading} onClick={() => handleSign('CONTRACTOR')}>I agree (Contractor)</button>
          )}
        </section>
      )}

      {/* Roof color — show homeowner-selected value to contractor */}
      {gate && (
        <section className="p0-roof-color" style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Roof color</h3>
          {materialSpec && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13 }}>
              {materialSpec.materialType && (
                <div><strong>Material Type:</strong> {materialSpec.materialType}</div>
              )}
              {(materialSpec.manufacturer || materialSpec.productLine) && (
                <div>
                  <strong>Material:</strong> {materialSpec.manufacturer} {materialSpec.productLine}
                </div>
              )}
              {materialSpec.colors && materialSpec.colors.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <strong>Color Options:</strong> {materialSpec.colors.join(', ')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {materialSpec.specUrl && (
                  <a href={materialSpec.specUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>View Product Page</a>
                )}
                {materialSpec.bidId && (
                  <a
                    href="#"
                    style={{ color: '#2563eb', textDecoration: 'underline' }}
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const { getApiUrl: _gau } = await import('../lib/getApiUrl');
                        const token = await getToken();
                        const res = await fetch(_gau(`/api/projects/${projectId}/bids/${materialSpec.bidId}/quote-pdf`), {
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                        });
                        if (!res.ok) { alert('Failed to load bid PDF'); return; }
                        const blob = await res.blob();
                        const objectUrl = URL.createObjectURL(blob);
                        window.open(objectUrl, '_blank', 'noopener,noreferrer');
                        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                      } catch { alert('Failed to load bid PDF'); }
                    }}
                  >
                    View Full Quote
                  </a>
                )}
              </div>
            </div>
          )}
          {gate.conditions.colorSelected ? (
            <p style={{ margin: 0, fontSize: 15 }}>Selected by homeowner: <strong>{roofColor || 'Color selected'}</strong></p>
          ) : (
            // CB:BEGIN canonical-color-select
            <>
              {colorOptionsFromAPI.length > 0 ? (
                <>
                  <select
                    value={selectedColorOptionId}
                    onChange={(e) => setSelectedColorOptionId(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 8 }}
                  >
                    <option value="">Select a color…</option>
                    {colorOptionsFromAPI.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.colorNameDisplay}{opt.distributorLabel ? ` — ${opt.distributorLabel}` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" disabled={!selectedColorOptionId || !!actionLoading} onClick={handleRoofColor}>Save</button>
                </>
              ) : availableColors.length > 0 ? (
                <>
                  <select value={roofColor} onChange={(e) => setRoofColor(e.target.value)} style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 8 }}>
                    <option value="">Select a color...</option>
                    {availableColors.map((color) => (
                      <option key={color.name} value={color.name} disabled={!color.available}>
                        {color.name} {!color.available ? '(Not available)' : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" disabled={!roofColor.trim() || !!actionLoading} onClick={handleRoofColor}>Save</button>
                </>
              ) : (
                <>
                  <input type="text" placeholder="e.g. Charcoal" value={roofColor} onChange={(e) => setRoofColor(e.target.value)} />
                  <button type="button" disabled={!roofColor.trim() || !!actionLoading} onClick={handleRoofColor}>Save</button>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6b7280' }}>No color options defined yet — contact your contractor to add options.</p>
                </>
              )}
            </>
            // CB:END canonical-color-select
          )}
        </section>
      )}

      {/* Scheduled start date — homeowner sets; contractor sees read-only */}
      {gate && (
        <section className="p0-scheduled-start" style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Start date</h3>
          {gate.scheduledStartAt ? (
            <p style={{ margin: 0, fontSize: 15 }}>Scheduled by homeowner: <strong>{new Date(gate.scheduledStartAt).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</strong></p>
          ) : canEdit && (role === 'homeowner' || role === 'admin') ? (
            <>
              <input
                type="datetime-local"
                value={scheduledStartInput}
                onChange={(e) => setScheduledStartInput(e.target.value)}
                style={{ marginRight: 8, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8 }}
              />
              <button type="button" className="btn btn-primary" disabled={!scheduledStartInput.trim() || !!actionLoading} onClick={handleSetScheduledStart}>Set start date</button>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>Not set yet. Homeowner will set the start date.</p>
          )}
        </section>
      )}

      {/* Start gate — Phase 6.3.1 checklist, 6.3.2 Start + tooltip, 6.3.4 admin override modal, 6.3.5 contractor message */}
      {gate && (
        <section className="p0-gate" style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Start project</h3>
          {role === 'contractor' && !gate.passed && (
            <p className="p0-contractor-waiting" style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>
              Waiting for homeowner to complete setup and start the project.
            </p>
          )}
          <ul style={{ margin: '0 0 12px 0', paddingLeft: 20, fontSize: 14, lineHeight: 1.8 }}>
            <li>Deposit captured: {(gate.conditions.depositCaptured ?? gate.conditions.escrowFunded) ? '✓' : '—'}</li>
            <li>Roof color selected: {gate.conditions.colorSelected ? '✓' : '—'}</li>
            <li>Contract signed: {gate.conditions.contractSigned ? '✓' : '—'}</li>
            <li>Milestone 1 (Project Start) defined: {gate.conditions.milestone1Defined ? '✓' : '—'}</li>
            <li>Scheduled start date set: {gate.conditions.scheduledStartSet ? '✓' : '—'}</li>
          </ul>
          {showStart && (
            <button type="button" disabled={!!actionLoading} onClick={() => handleStart()}>
              Start project
            </button>
          )}
          {canEdit && !showStart && gate && missingConditions.length > 0 && (
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }} title={missingConditions.join(', ')}>
              Complete: {missingConditions.join(', ')} to enable Start project.
            </p>
          )}
          {role === 'admin' && !gate.passed && (
            <div style={{ marginTop: 8 }}>
              <button type="button" disabled={!!actionLoading} onClick={() => setShowOverrideModal(true)}>
                Override gate
              </button>
            </div>
          )}
          {showOverrideModal && (
            <div role="dialog" aria-label="Override start gate" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 320 }}>
                <h4 style={{ marginTop: 0 }}>Override start gate</h4>
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>Provide a reason (required for audit).</p>
                <textarea
                  value={adminOverrideReason}
                  onChange={(e) => setAdminOverrideReason(e.target.value)}
                  placeholder="Reason for override"
                  rows={3}
                  style={{ width: '100%', marginBottom: 12, padding: 8 }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setShowOverrideModal(false); setAdminOverrideReason(''); }}>Cancel</button>
                  <button type="button" disabled={!adminOverrideReason.trim() || !!actionLoading} onClick={() => handleStart(adminOverrideReason.trim())}>
                    Override and start
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* CB:BEGIN p0-off-platform-policy — FR-017: visible on project flow */}
      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 16 }}>
        Work and payments through CoreBids; going off-platform may void guarantees and dispute support.{' '}
        <a href="/payment-terms" target="_blank" rel="noopener noreferrer">Payment terms</a>
      </p>
      {/* CB:END p0-off-platform-policy */}

      {disputeModal && (
        <MilestoneDisputeModal
          projectId={projectId}
          milestoneKey={disputeModal.milestoneKey}
          milestoneName={disputeModal.milestoneName}
          projectAddress={(escrow as any)?.project?.address}
          onClose={() => setDisputeModal(null)}
        />
      )}
    </div>
  );
};

export default P0ProjectFlow;
// CB:END p0-fr-016-017-022-project-flow
